import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle, ChevronDown, ChevronRight, DollarSign, Clock, ExternalLink, Info } from 'lucide-react';
import MessageList from '../pages/trace-detail/components/shared/MessageList';
import ToolSchemasChevron from '../pages/trace-detail/components/shared/ToolSchemasChevron';
import Accordion from '../pages/trace-detail/components/shared/Accordion';
import JSONCollapsibleViewer from '../pages/trace-detail/components/shared/JSONCollapsibleViewer';
import { ToolCard, parseDoubleEncoded } from '../pages/trace-detail/components/shared/ToolCard';
import CopyButton from './CopyButton';
import { palette, withOpacity } from '../utils/designTokens';
import { normalizeInspectorMessages } from '../utils/spanInspectorMessages';

const fmtMs = ms => {
  if (ms == null) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
};

const fmtCost = usd => (usd != null ? `$${usd.toFixed(6)}` : '—');

// Shared so model/cost/latency read as one consistent row, not three different weights.
const OUTPUT_META_STYLE = { fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: palette.text.primary };

const DRAWER_WIDTH_KEY = 'lumoz-span-inspector-width';
const DEFAULT_DRAWER_WIDTH = 720;
const MIN_DRAWER_WIDTH = 420;

const getStoredWidth = () => {
  try {
    const stored = Number(window.localStorage.getItem(DRAWER_WIDTH_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_DRAWER_WIDTH;
  } catch {
    return DEFAULT_DRAWER_WIDTH;
  }
};

/**
 * One side of the output comparison — original or replay. Assistant text and
 * tool calls both render when present; a replay error takes over the whole pane
 * since there's no output to show alongside it.
 */
const OutputPane = ({ label, output, toolCalls, model, costUsd, latencyMs, error, accentColor }) => {
  const hasToolCalls = Array.isArray(toolCalls) && toolCalls.length > 0;
  return (
    <div className="flex flex-col gap-2 min-w-0" style={{ flex: 1 }}>
      <div className="flex items-center gap-3">
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: accentColor }}>
          {label}
        </span>
        <div className="flex items-center gap-3" style={{ marginLeft: 'auto' }}>
          {model && <span style={OUTPUT_META_STYLE}>{model}</span>}
          {costUsd != null && (
            <span className="flex items-center gap-1">
              <DollarSign size={12} style={{ color: palette.text.muted }} />
              <span style={OUTPUT_META_STYLE}>{fmtCost(costUsd)}</span>
            </span>
          )}
          {latencyMs != null && (
            <span className="flex items-center gap-1">
              <Clock size={12} style={{ color: palette.text.muted }} />
              <span style={OUTPUT_META_STYLE}>{fmtMs(latencyMs)}</span>
            </span>
          )}
        </div>
      </div>
      <div
        className="rounded-lg p-3 overflow-auto"
        style={{
          minHeight: 96,
          maxHeight: 320,
          backgroundColor: palette.neutrals.surfaceSubtle,
          border: `1px solid ${error ? `${palette.status.danger}50` : palette.borders.subtle}`,
        }}
      >
        {error ? (
          <div className="flex items-start gap-2">
            <AlertTriangle size={13} style={{ color: palette.status.danger, flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: 12, color: palette.status.danger, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</span>
          </div>
        ) : output || hasToolCalls ? (
          <div className="space-y-2">
            {output && (
              <div
                style={{
                  fontSize: 12,
                  color: palette.text.primary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {output}
              </div>
            )}
            {toolCalls.map((tc, i) => (
              <ToolCard
                key={tc.id || i}
                name={tc.function?.name || tc.name || 'tool'}
                data={parseDoubleEncoded(tc.function?.arguments ?? tc.arguments)}
              />
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: output ? palette.text.primary : palette.text.muted, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {output || 'No output'}
          </span>
        )}
      </div>
    </div>
  );
};

/** Prominent above-the-fold badge for a diff score — the signal that says "look closer", before the reader digs into the actual text. */
const DiffPill = ({ label, value, color }) => (
  <div
    className="flex items-baseline gap-1.5 rounded-lg px-3 py-1.5"
    style={{ backgroundColor: withOpacity(color, 12), border: `1px solid ${withOpacity(color, 30)}` }}
  >
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color }}>{label}</span>
    <span style={{ fontSize: 15, fontWeight: 700, color }}>{value}</span>
  </div>
);

/** Plain divider label for a group of accordion items — not itself collapsible, so
 * getting to e.g. "System prompt" is one click, not two. */
const GROUP_TITLE_STYLE = { fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: palette.text.muted };

// Same divider look either way — onClick turns it into a collapsible group header
// (with a chevron) instead of a static label, for groups worth hiding by default.
const GroupTitle = ({ children, onClick, expanded }) => (
  onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1"
      style={{ ...GROUP_TITLE_STYLE, padding: '2px 8px', background: 'none', border: 'none', cursor: 'pointer' }}
    >
      {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      {children}
    </button>
  ) : (
    <div style={{ ...GROUP_TITLE_STYLE, padding: '2px 8px 0' }}>
      {children}
    </div>
  )
);

const StatRow = ({ label, original, replay }) => (
  <>
    <span style={{ color: palette.text.muted, fontSize: 12 }}>{label}</span>
    <span style={{ color: palette.text.primary, fontSize: 12, fontFamily: 'monospace' }}>{original ?? '—'}</span>
    <span style={{ color: palette.text.primary, fontSize: 12, fontFamily: 'monospace' }}>{replay ?? '—'}</span>
  </>
);

const DebugInputPane = ({ label, data, emptyText, accentColor }) => (
  <div className="flex flex-col gap-2 min-w-0">
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: accentColor }}>
      {label}
    </span>
    <div
      className="rounded p-2.5 min-w-0 overflow-auto"
      style={{
        maxHeight: 420,
        backgroundColor: palette.neutrals.surfaceSubtle,
        border: `1px solid ${palette.borders.subtle}`,
      }}
    >
      {data ? (
        <JSONCollapsibleViewer data={data} />
      ) : (
        <span style={{ fontSize: 12, color: palette.text.muted }}>{emptyText}</span>
      )}
    </div>
  </div>
);

const DebugInputComparison = ({ originalInput, replayInput }) => (
  <div
    className="grid gap-3"
    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
  >
    <DebugInputPane
      label="Original input"
      data={originalInput}
      emptyText="Original input unavailable."
      accentColor={palette.text.muted}
    />
    <DebugInputPane
      label="Replay input"
      data={replayInput}
      emptyText="Replay input unavailable for this run."
      accentColor={palette.accents.primary.base}
    />
  </div>
);

/**
 * SpanInspector — slide-in drawer for one LLM span's captured input and (when a
 * replay ran) the original/replay output comparison. Same interaction pattern as
 * PolicyDetailDrawer (animate in/out, backdrop click + Escape to close).
 *
 * Content is layered by how often it's actually needed:
 *   1. Output comparison (original vs replay) — the thing you opened this for.
 *   2. "Input" accordion — system prompt, sample input, tool schemas.
 *   3. "Details" accordion — token/cost/latency table, then raw JSON for both
 *      sides, so nothing is ever hidden, just not competing for space by default.
 *
 * When `details.original`/`details.replay` are absent (no run yet — the
 * operation config list preview case), only the Input section renders.
 */
const SpanInspector = ({ isOpen, onClose, title, subtitle, loading, error, details, contextTitle, contextDescription, contextAction }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [width, setWidth] = useState(getStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [debugExpanded, setDebugExpanded] = useState(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
    } else if (isAnimating) {
      setIsVisible(false);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Drag-to-resize: anchored to the right edge, so width = distance from the
  // pointer to the right edge of the viewport. Clamped between a usable
  // minimum and the full viewport width ("all the way to max").
  useEffect(() => {
    const onMouseMove = e => {
      if (!draggingRef.current) return;
      const next = Math.min(Math.max(window.innerWidth - e.clientX, MIN_DRAWER_WIDTH), window.innerWidth);
      setWidth(next);
    };
    const onMouseUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setWidth(current => {
        try { window.localStorage.setItem(DRAWER_WIDTH_KEY, String(current)); } catch { /* per-viewer convenience only */ }
        return current;
      });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResize = e => {
    e.preventDefault();
    draggingRef.current = true;
    setIsResizing(true);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  if (!isAnimating) return null;

  const clampedWidth = Math.min(width, window.innerWidth);

  const original = details?.original || {};
  const replay = details?.replay || {};
  const input = details?.input || {};
  // The stored `original` record is input+output+metadata combined (replay is
  // fixed-input by design, so it never needed the input fields — but original
  // does carry them). Strip the input-side keys here so the "Original output"
  // debug dump doesn't re-show system_prompt/messages that are already in the
  // "Original input" section right above it.
  const {
    system_prompt: _originalSystemPrompt,
    user_message: _originalUserMessage,
    messages: _originalMessages,
    input_messages_source: _originalInputMessagesSource,
    input_messages_truncated: _originalInputMessagesTruncated,
    input_messages_parse_error: _originalInputMessagesParseError,
    tool_schemas: _originalToolSchemas,
    tool_schemas_truncated: _originalToolSchemasTruncated,
    tool_schemas_parse_error: _originalToolSchemasParseError,
    ...originalOutputDebug
  } = original;
  const {
    replay_input: replayInput,
    ...replayOutputDebug
  } = replay;
  const originalToolCalls = details?.original_tool_calls || [];
  const replayToolCalls = details?.replay_tool_calls || [];
  const hasRun = Boolean(details && (details.original || details.replay));
  const responseTypeChanged = hasRun && !replay.error &&
    (originalToolCalls.length > 0) !== (replayToolCalls.length > 0);

  const costPct = original.cost_usd > 0 ? Math.round((replay.cost_usd - original.cost_usd) / original.cost_usd * 100) : null;
  const latencyPct = original.latency_ms > 0 ? Math.round((replay.latency_ms - original.latency_ms) / original.latency_ms * 100) : null;

  // Some spans (e.g. Mastra-instrumented) store the user turn as the flat
  // `user_message` string instead of populating the structured `messages`
  // array — fall back to it so the turn isn't silently dropped from view.
  const messagesFromArray = normalizeInspectorMessages(input.messages).filter(m => m?.role && m.role !== 'system');
  const sampleMessages = messagesFromArray.length > 0
    ? messagesFromArray
    : (input.user_message ? [{ role: 'user', content: input.user_message }] : []);

  // Only the settings that actually differ from "whatever the original call used" —
  // the internal ids/agent/span-name bookkeeping in overrides_applied isn't config.
  const overridesApplied = details?.overrides_applied || {};
  const configRows = [
    overridesApplied.provider && { label: 'Provider', value: overridesApplied.provider },
    overridesApplied.temperature != null && { label: 'Temperature', value: String(overridesApplied.temperature) },
    overridesApplied.max_tokens != null && { label: 'Max tokens', value: String(overridesApplied.max_tokens) },
    overridesApplied.system_prompt && { label: 'System prompt', value: 'Overridden — see Input section for the original' },
  ].filter(Boolean);

  const hasSystemPrompt = Boolean(input.system_prompt);
  const hasSampleMessages = sampleMessages.length > 0;
  const hasTools = Array.isArray(input.tool_schemas) && input.tool_schemas.length > 0;
  const hasInputGroup = hasSystemPrompt || hasSampleMessages || hasTools;

  return (
    <>
      <div
        className="fixed top-16 left-0 right-0 bottom-0 transition-opacity duration-300"
        style={{ zIndex: 40, backgroundColor: palette.quality.overlayModal, opacity: isVisible ? 1 : 0 }}
        onClick={onClose}
      />
      <div
        className={`fixed top-16 right-0 bottom-0 flex flex-col ease-out ${isResizing ? '' : 'transition-transform duration-300'}`}
        style={{
          zIndex: 50,
          width: clampedWidth,
          maxWidth: '100vw',
          backgroundColor: palette.neutrals.canvas,
          borderLeft: `1px solid ${palette.borders.medium}`,
          boxShadow: palette.quality.shadowDrawer,
          transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Drag handle — resizes down to MIN_DRAWER_WIDTH, up to the full viewport width */}
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: -4,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 51,
            backgroundColor: isResizing ? withOpacity(palette.accents.primary.base, 40) : 'transparent',
          }}
        />
        <div className="flex items-start justify-between gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm font-semibold truncate" style={{ color: palette.text.primary }}>{title}</span>
            {subtitle && <span className="text-xs truncate" style={{ color: palette.text.muted }}>{subtitle}</span>}
          </div>
          <button type="button" onClick={onClose} style={{ color: palette.text.muted, flexShrink: 0 }} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-4">
          {(contextTitle || contextDescription || contextAction) && (
            <div
              className="flex items-center gap-3 rounded-lg p-3"
              style={{ backgroundColor: palette.neutrals.surfaceMid, border: `1px solid ${palette.borders.subtle}` }}
            >
              <Info size={13} style={{ color: palette.accents.primary.base, flexShrink: 0 }} />
              <div className="flex flex-col gap-0.5 min-w-0">
                {contextTitle && <span style={{ fontSize: 12, fontWeight: 700, color: palette.text.primary }}>{contextTitle}</span>}
                {contextDescription && <span style={{ fontSize: 12, color: palette.text.muted }}>{contextDescription}</span>}
              </div>
              {contextAction?.href && (
                <a
                  href={contextAction.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1"
                  style={{
                    marginLeft: 'auto',
                    padding: '3px 8px',
                    borderRadius: 5,
                    border: `1px solid ${palette.borders.subtle}`,
                    color: palette.accents.primary.base,
                    fontSize: 11,
                    fontWeight: 700,
                    textDecoration: 'none',
                    flexShrink: 0,
                  }}
                >
                  {contextAction.label || 'Open trace'}
                  <ExternalLink size={11} />
                </a>
              )}
            </div>
          )}
          {loading ? (
            <span style={{ fontSize: 12, color: palette.text.muted }}>Loading…</span>
          ) : error ? (
            <div className="rounded-lg p-3" style={{ backgroundColor: `${palette.status.danger}10`, border: `1px solid ${palette.status.danger}30`, color: palette.status.danger, fontSize: 12 }}>
              {error}
            </div>
          ) : !details ? (
            <span style={{ fontSize: 12, color: palette.text.muted }}>No details available for this span.</span>
          ) : (
            <>
              {responseTypeChanged && (
                <div className="flex items-center gap-2 rounded-lg p-2.5" style={{ backgroundColor: `${palette.status.warning}12`, border: `1px solid ${palette.status.warning}30` }}>
                  <AlertTriangle size={13} style={{ color: palette.status.warning, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: palette.text.primary }}>
                    Response type changed — {originalToolCalls.length > 0 ? 'tool call → text' : 'text → tool call'}
                  </span>
                </div>
              )}

              {hasRun && (
                <>
                  {(costPct != null || latencyPct != null || details.similarity != null || details.semantic_similarity != null) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {costPct != null && (
                        <DiffPill label="Cost" value={`${costPct > 0 ? '+' : ''}${costPct}%`} color={costPct <= 0 ? palette.status.success : palette.status.danger} />
                      )}
                      {latencyPct != null && (
                        <DiffPill label="Latency" value={`${latencyPct > 0 ? '+' : ''}${latencyPct}%`} color={latencyPct <= 0 ? palette.status.success : palette.status.danger} />
                      )}
                      {details.similarity != null && (
                        <DiffPill label="Text similarity" value={`${Math.round(details.similarity * 100)}%`} color={palette.accents.warm.base} />
                      )}
                      {details.semantic_similarity != null && (
                        <DiffPill label="Semantic similarity" value={`${Math.round(details.semantic_similarity * 100)}%`} color={palette.accents.primary.base} />
                      )}
                    </div>
                  )}

                  <GroupTitle>Replay</GroupTitle>
                  <Accordion sections={[
                    {
                      title: 'Output',
                      defaultExpanded: true,
                      content: (
                        <div className="flex gap-4">
                          <OutputPane
                            label="Original"
                            output={details.original_output}
                            toolCalls={originalToolCalls}
                            model={original.model}
                            costUsd={original.cost_usd}
                            latencyMs={original.latency_ms}
                            accentColor={palette.text.muted}
                          />
                          <OutputPane
                            label="Replay"
                            output={details.replay_output}
                            toolCalls={replayToolCalls}
                            model={replay.model}
                            costUsd={replay.cost_usd}
                            latencyMs={replay.latency_ms}
                            error={replay.error}
                            accentColor={palette.accents.primary.base}
                          />
                        </div>
                      ),
                    },
                    {
                      title: 'Stats',
                      defaultExpanded: false,
                      content: (
                        <div
                          className="rounded p-2.5 grid gap-x-4 gap-y-1.5"
                          style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}`, gridTemplateColumns: 'auto 1fr 1fr' }}
                        >
                          <span />
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: palette.text.muted }}>Original</span>
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: palette.text.muted }}>Replay</span>
                          <StatRow label="Input tokens" original={original.input_tokens} replay={replay.input_tokens} />
                          <StatRow label="Output tokens" original={original.output_tokens} replay={replay.output_tokens} />
                          <StatRow label="Cost" original={fmtCost(original.cost_usd)} replay={fmtCost(replay.cost_usd)} />
                          <StatRow label="Latency" original={fmtMs(original.latency_ms)} replay={fmtMs(replay.latency_ms)} />
                        </div>
                      ),
                    },
                    {
                      title: 'Config',
                      defaultExpanded: false,
                      content: (
                        <div
                          className="rounded p-2.5 grid gap-x-4 gap-y-1.5"
                          style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}`, gridTemplateColumns: 'auto 1fr' }}
                        >
                          <span style={{ fontSize: 12, color: palette.text.muted }}>Model</span>
                          <span style={{ fontSize: 12, fontFamily: 'monospace', color: palette.text.primary }}>
                            {original.model || '—'}
                            {replay.model && replay.model !== original.model && (
                              <>
                                {' → '}
                                <span style={{ color: palette.accents.primary.base, fontWeight: 600 }}>{replay.model}</span>
                              </>
                            )}
                          </span>
                          {configRows.map(row => (
                            <React.Fragment key={row.label}>
                              <span style={{ fontSize: 12, color: palette.text.muted }}>{row.label}</span>
                              <span style={{ fontSize: 12, fontFamily: 'monospace', color: palette.text.primary }}>{row.value}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      ),
                    },
                  ].filter(Boolean)} scrollable={false} />
                </>
              )}

              {hasInputGroup && (
                <>
                  <GroupTitle>Input</GroupTitle>
                  <Accordion sections={[
                    hasSystemPrompt && {
                      title: 'System prompt',
                      defaultExpanded: !hasRun,
                      content: (
                        <div>
                          <div className="flex items-center justify-end mb-1">
                            <CopyButton text={input.system_prompt} size="xs" label="Copy system prompt" />
                          </div>
                          <div
                            className="rounded p-2.5"
                            style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}`, fontSize: 12, color: palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                          >
                            {input.system_prompt}
                          </div>
                        </div>
                      ),
                    },
                    hasSampleMessages && {
                      // "Sample input" only when this preview isn't tied to one specific
                      // execution (the operation config list, before any replay has run) —
                      // an operation can be backed by many spans, so it's a representative
                      // example, not the input. Once a replay has actually happened for this
                      // one span, it's the exact input that was sent — call it what it is.
                      title: hasRun ? 'User input' : 'Sample input',
                      defaultExpanded: !hasRun,
                      content: <MessageList messages={sampleMessages} type="input" background={palette.neutrals.surfaceSubtle} />,
                    },
                    hasTools && {
                      title: 'Tools',
                      defaultExpanded: !hasRun,
                      content: <ToolSchemasChevron tools={input.tool_schemas} />,
                    },
                  ].filter(Boolean)} scrollable={false} />
                </>
              )}

              {(hasInputGroup || hasRun) && (
                <>
                  <GroupTitle onClick={() => setDebugExpanded(v => !v)} expanded={debugExpanded}>Debug</GroupTitle>
                  {debugExpanded && (
                    <Accordion sections={[
                      (hasInputGroup || hasRun) && {
                        title: 'Input comparison',
                        defaultExpanded: true,
                        content: <DebugInputComparison originalInput={input} replayInput={replayInput} />,
                      },
                      hasRun && {
                        title: 'Original output',
                        defaultExpanded: false,
                        content: <JSONCollapsibleViewer data={originalOutputDebug} />,
                      },
                      hasRun && {
                        title: 'Replay output',
                        defaultExpanded: false,
                        content: <JSONCollapsibleViewer data={replayOutputDebug} />,
                      },
                    ].filter(Boolean)} scrollable={false} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SpanInspector;
